import {Bell, BellDot} from "lucide-react";
import {useState} from "react";

import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import {useStoredMe} from "@/src/modules/core/store/me";

import {useBellUnreadCount} from "../hooks/use-alerts-feed-unread-count";
import {getNotificationBellQueryParams} from "../lib/notification-feed-topics";
import {useNotificationFeed} from "../hooks/use-notification-feed";
import {NotificationsSheet} from "./notifications-sheet";

type NotificationFeedBellButtonProps = {
  showLabel?: boolean;
  className?: string;
  buttonClassName?: string;
  badgeVariant?: "count" | "dot";
};

const BADGE_MAX = 99;

export function NotificationFeedBellButton({
  showLabel = false,
  className,
  buttonClassName,
  badgeVariant = "count",
}: NotificationFeedBellButtonProps) {
  const [open, setOpen] = useState(false);
  const me = useStoredMe();
  const isResident = Boolean(me?.isResident);
  const bellQueryParams = getNotificationBellQueryParams(isResident);
  useNotificationFeed({params: bellQueryParams});
  const unreadCount = useBellUnreadCount();
  const hasUnread = unreadCount > 0;
  const badgeLabel = unreadCount > BADGE_MAX ? `${BADGE_MAX}+` : unreadCount.toString();
  const showInlineBadge = showLabel && badgeVariant === "count";
  const useIntegratedDot = hasUnread && badgeVariant === "dot";
  /** Icon-only control: make unread state feel actionable (not faint outline ink). */
  const emphasizeUnreadChrome = hasUnread && !showLabel;
  const iconStroke = emphasizeUnreadChrome ? 2.75 : 2;

  return (
    <>
      <div className={cn("relative", className)}>
        <Button
          type="button"
          variant={
            showLabel ? "ghost" : emphasizeUnreadChrome ? "destructiveOutline" : "outline"
          }
          size={showLabel ? "sm" : "icon"}
          className={cn(
            "relative",
            emphasizeUnreadChrome && "bg-destructive/10",
            buttonClassName,
          )}
          aria-label={
            hasUnread ? `Open alerts, ${unreadCount} unread` : "Open alerts"
          }
          onClick={() => setOpen(true)}
        >
          {useIntegratedDot ? (
            <BellDot strokeWidth={iconStroke} aria-hidden />
          ) : (
            <Bell strokeWidth={iconStroke} aria-hidden />
          )}
          {showLabel ? <span>Notifications</span> : null}
          {hasUnread && showInlineBadge ? (
            <span className="bg-destructive text-white inline-flex min-w-6 items-center justify-center rounded-full p-1 text-xs leading-none font-bold">
              {badgeLabel}
            </span>
          ) : null}
          {hasUnread && !showInlineBadge && badgeVariant === "count" ? (
            <span
              className={cn(
                "pointer-events-none absolute bottom-1 right-1 z-10 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-white",
              )}
            >
              {badgeLabel}
            </span>
          ) : null}
        </Button>
      </div>
      <NotificationsSheet open={open} onOpenChange={setOpen} />
    </>
  );
}

